import React from 'react';
import { Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import { remapSafetyScanCustomerLabel } from '../../domain/acceptance/safetyScanPresentationCopy';
import type { ExternalSafetyReviewModel } from '../../domain/externalReview/toExternalSafetyReviewModel';

type OverviewRow = {
  label: string;
  value: string;
  testId?: string;
};

function formatEvidenceIncluded(model: ExternalSafetyReviewModel): string {
  const summary = model.evidenceSummary || {};
  const signals = summary.observed_signals ?? summary.event_signals;
  const shown = summary.representative_evidence_shown;
  const parts: string[] = [];
  if (signals != null && signals !== '') {
    parts.push(`${signals} measured signals summarized`);
  }
  if (shown != null && shown !== '') {
    parts.push(`${shown} representative evidence sample(s) included`);
  }
  const rep = model.representativeEvidence;
  if (Array.isArray(rep)) {
    parts.push(`${rep.length} representative media item(s)`);
  } else if (rep && typeof rep === 'object' && Array.isArray((rep as Record<string, unknown>).items)) {
    parts.push(`${((rep as Record<string, unknown>).items as unknown[]).length} representative media item(s)`);
  }
  return parts.join(' · ') || 'Evidence summary included in external-safe package';
}

function formatClaimBoundaries(model: ExternalSafetyReviewModel): string {
  const claims = model.claimBoundaries || {};
  const supported = Array.isArray(claims.supported) ? claims.supported.filter(Boolean) : [];
  const unsupported = Array.isArray(claims.unsupported) ? claims.unsupported.filter(Boolean) : [];
  const parts: string[] = [];
  if (supported.length) {
    parts.push(`Supported: ${supported.slice(0, 3).join('; ')}`);
  }
  if (unsupported.length) {
    parts.push(`Not claimed: ${unsupported.slice(0, 3).join('; ')}`);
  }
  return parts.join(' · ') || 'No exposure-reduction or effectiveness claim from this review package.';
}

type Props = {
  model: ExternalSafetyReviewModel;
};

export default function ExternalReviewPackageOverview({ model }: Props) {
  const linked = model.linkedSafetyCase;
  const occurrence = model.occurrenceSummary;
  const reviewState = model.reviewActionState;

  const rows: OverviewRow[] = [
    {
      label: 'What happened',
      value: model.workplaceConditionObserved.conditionLabel,
      testId: 'external-overview-what-happened',
    },
    {
      label: 'Where',
      value: model.workplaceConditionObserved.areaLabel || 'Site area',
      testId: 'external-overview-where',
    },
    {
      label: 'How often',
      value: remapSafetyScanCustomerLabel(String(occurrence?.summary || occurrence?.observed_signals || 'Frequency summarized in scan window')),
      testId: 'external-overview-how-often',
    },
    {
      label: 'Linked Safety Case',
      value: linked?.safetyCaseId
        ? `${linked.label || 'Safety Case'} (${linked.safetyCaseId})${linked.statusLabel ? ` — ${linked.statusLabel}` : ''}`
        : 'Linked Safety Case reference included in signed scan',
      testId: 'external-overview-linked-case',
    },
    {
      label: 'Evidence included',
      value: formatEvidenceIncluded(model),
      testId: 'external-overview-evidence',
    },
    {
      label: 'Review / action state',
      value: String(
        reviewState?.summary
          || reviewState?.corrective_action_label
          || model.followUpReadiness.summary
          || model.followUpReadiness.label
          || 'Review and corrective-action status recorded',
      ),
      testId: 'external-overview-review-state',
    },
    {
      label: 'Downloadable package',
      value: 'External-safe Signed Safety Scan PDF and ZIP (no internal appendix or customer-only exports)',
      testId: 'external-overview-download',
    },
    {
      label: 'Claim boundaries',
      value: formatClaimBoundaries(model),
      testId: 'external-overview-claim-boundaries',
    },
  ];

  return (
    <Card variant="outlined" data-testid="external-review-package-overview">
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h5" fontWeight={700}>
            Safety Review Package
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Read-only external review summary. This package is for workplace-condition review — not worker scoring,
            ranking, or disciplinary use.
          </Typography>
          <Grid container spacing={2}>
            {rows.map((row) => (
              <Grid item xs={12} md={6} key={row.label}>
                <Stack spacing={0.5} data-testid={row.testId}>
                  <Typography variant="overline" color="text.secondary" lineHeight={1.2}>
                    {row.label}
                  </Typography>
                  <Typography variant="body1">{row.value}</Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </CardContent>
    </Card>
  );
}
